import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import AnimatedSection from '../components/AnimatedSection';
import { useLanguage } from '../i18n/pageTranslations';

const SegundaOpinion = () => {
  const lang = useLanguage();

  const texts = {
    es: {
      eyebrow: 'Cirugía Reparadora y Pericial',
      title: 'Segunda Opinión',
      description: 'Valoración médica independiente antes de tomar una decisión importante. Análisis objetivo de su caso y de las opciones de tratamiento disponibles.',
      requestAssessment: 'Solicitar Valoración',
      callNow: 'Llamar Ahora',
      whenTitle: '¿Cuándo solicitar una segunda opinión?',
      whenItems: [
        'Antes de someterse a una cirugía plástica importante',
        'Si tiene dudas sobre el diagnóstico o tratamiento propuesto',
        'Si el resultado esperado no le convence',
        'Antes de una cirugía de revisión',
        'Para confirmar o contrastar una recomendación quirúrgica',
      ],
      whatIncludesTitle: '¿Qué incluye?',
      whatIncludesItems: [
        'Revisión completa de su historial médico',
        'Examen físico detallado',
        'Análisis de pruebas y estudios previos',
        'Valoración objetiva de opciones de tratamiento',
        'Informe médico por escrito si lo solicita',
      ],
      quote: '"Una segunda opinión no es desconfianza hacia su médico. Es prudencia y responsabilidad con su salud."',
      ctaTitle: '¿Necesita una segunda opinión?',
      ctaDesc: 'Solicite una consulta de valoración independiente.',
      contact: 'Contactar',
    },
    en: {
      eyebrow: 'Reconstructive & Expert Surgery',
      title: 'Second Opinion',
      description: 'Independent medical assessment before making an important decision. Objective analysis of your case and available treatment options.',
      requestAssessment: 'Request Assessment',
      callNow: 'Call Now',
      whenTitle: 'When to request a second opinion?',
      whenItems: [
        'Before undergoing major plastic surgery',
        'If you have doubts about the proposed diagnosis or treatment',
        'If the expected result does not convince you',
        'Before revision surgery',
        'To confirm or contrast a surgical recommendation',
      ],
      whatIncludesTitle: 'What does it include?',
      whatIncludesItems: [
        'Complete review of your medical history',
        'Detailed physical examination',
        'Analysis of previous tests and studies',
        'Objective assessment of treatment options',
        'Written medical report if requested',
      ],
      quote: '"A second opinion is not distrust of your doctor. It is prudence and responsibility with your health."',
      ctaTitle: 'Need a second opinion?',
      ctaDesc: 'Request an independent assessment consultation.',
      contact: 'Contact',
    },
    fr: {
      eyebrow: 'Chirurgie Réparatrice et Expertale',
      title: 'Deuxième Avis',
      description: 'Évaluation médicale indépendante avant de prendre une décision importante. Analyse objective de votre cas et des options de traitement disponibles.',
      requestAssessment: 'Demander une Évaluation',
      callNow: 'Appeler Maintenant',
      whenTitle: 'Quand demander un deuxième avis?',
      whenItems: [
        'Avant de subir une chirurgie plastique importante',
        'Si vous avez des doutes sur le diagnostic ou le traitement proposé',
        'Si le résultat attendu ne vous convainc pas',
        'Avant une chirurgie de révision',
        'Pour confirmer ou contraster une recommandation chirurgicale',
      ],
      whatIncludesTitle: 'Que comprend-il?',
      whatIncludesItems: [
        'Révision complète de votre dossier médical',
        'Examen physique détaillé',
        'Analyse des examens et études précédents',
        'Évaluation objective des options de traitement',
        'Rapport médical écrit sur demande',
      ],
      quote: '"Un deuxième avis n\'est pas de la méfiance envers votre médecin. C\'est de la prudence et de la responsabilité envers votre santé."',
      ctaTitle: 'Besoin d\'un deuxième avis?',
      ctaDesc: 'Demandez une consultation d\'évaluation indépendante.',
      contact: 'Contacter',
    },
    pt: {
      eyebrow: 'Cirurgia Reparadora e Pericial',
      title: 'Segunda Opinião',
      description: 'Avaliação médica independente antes de tomar uma decisão importante. Análise objetiva do seu caso e das opções de tratamento disponíveis.',
      requestAssessment: 'Solicitar Avaliação',
      callNow: 'Ligar Agora',
      whenTitle: 'Quando solicitar uma segunda opinião?',
      whenItems: [
        'Antes de se submeter a uma cirurgia plástica importante',
        'Se você tem dúvidas sobre o diagnóstico ou tratamento proposto',
        'Se o resultado esperado não o convence',
        'Antes de uma cirurgia de revisão',
        'Para confirmar ou contrastar uma recomendação cirúrgica',
      ],
      whatIncludesTitle: 'O que inclui?',
      whatIncludesItems: [
        'Revisão completa do seu histórico médico',
        'Exame físico detalhado',
        'Análise de exames e estudos prévios',
        'Avaliação objetiva das opções de tratamento',
        'Relatório médico por escrito se solicitado',
      ],
      quote: '"Uma segunda opinião não é desconfiança em relação ao seu médico. É prudência e responsabilidade com sua saúde."',
      ctaTitle: 'Precisa de uma segunda opinião?',
      ctaDesc: 'Solicite uma consulta de avaliação independente.',
      contact: 'Contatar',
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <div className="min-h-screen bg-[#FDFBF8]">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="fadeRight">
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.eyebrow}</p>
              <h1 className="font-serif text-5xl md:text-6xl text-[#1C1C1A] mb-6 leading-tight">{t.title}</h1>
              <div className="w-20 h-[2px] bg-[#B8975A] mb-8"></div>
              <p className="text-lg text-[#4A4845] leading-relaxed mb-8">{t.description}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contacto" className="inline-flex items-center justify-center gap-2 bg-[#B8975A] text-white px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
                  {t.requestAssessment}
                  <ArrowRight size={16} />
                </Link>
                <a href="tel:+34952764547" className="inline-flex items-center justify-center gap-2 border border-[#1C1C1A] text-[#1C1C1A] px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#1C1C1A] hover:text-white transition-colors">
                  <Phone size={16} />
                  {t.callNow}
                </a>
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fadeLeft" className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-[#B8975A]"></div>
              <img src="https://images.pexels.com/photos/5699456/pexels-photo-5699456.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Segunda Opinión" className="w-full h-auto" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[#B8975A]"></div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* When & What */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-16">
            <AnimatedSection animation="fadeRight">
              <h2 className="font-serif text-3xl text-white mb-8">{t.whenTitle}</h2>
              <div className="space-y-4">
                {t.whenItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-[#B8975A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} className="text-[#B8975A]" />
                    </div>
                    <span className="text-white/80">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fadeLeft">
              <h2 className="font-serif text-3xl text-white mb-8">{t.whatIncludesTitle}</h2>
              <div className="space-y-4">
                {t.whatIncludesItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-[#B8975A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} className="text-[#B8975A]" />
                    </div>
                    <span className="text-white/80">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-20 bg-[#F0EDE7]">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <AnimatedSection animation="fadeUp">
            <blockquote className="font-serif text-2xl md:text-3xl text-[#1C1C1A] italic leading-relaxed">
              {t.quote}
            </blockquote>
            <p className="text-[#B8975A] mt-6">— Dr. Ángel Norro</p>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <AnimatedSection animation="fadeUp">
            <h2 className="font-serif text-4xl text-[#1C1C1A] mb-6">{t.ctaTitle}</h2>
            <p className="text-[#4A4845] mb-10">{t.ctaDesc}</p>
            <Link to="/contacto" className="inline-flex items-center gap-2 bg-[#B8975A] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
              {t.contact}
              <ArrowRight size={16} />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default SegundaOpinion;
